import { CommunityChannelApi } from '@ps/api/community-channel';
import { withClientAPI } from '@ps/functions/utils/api';
import {
  type CommunityChannelMessageAttachment,
  type CommunityChannelMessageLink,
  CommunityChannelMessageTextContentType,
} from '@ps/models/lib/community-channel';

/**
 * Post a message into a channel. The server stamps the
 * authoritative `id` / `createdAt` and we get the new id back; live
 * updates (this message landing in the message-list state) come
 * through the Firestore subscription on the same channel, so callers
 * don't need to re-fetch after a send.
 *
 * The optimistic bubble keyed by `clientMessageId` should be
 * reconciled with the Firestore row once it arrives — the row uses
 * the same `clientMessageId` field for matching.
 *
 * `contentType` defaults to `plain-text` so legacy callers that
 * only have a string keep working; the rich-text composer passes
 * `slatejs-dom` with the JSON-encoded Slate tree.
 */
export async function sendChannelMessage({
  creatorId,
  channelId,
  clientMessageId,
  content,
  contentType = CommunityChannelMessageTextContentType.PlainText,
  parentMessageId,
  attachments,
  links,
}: {
  creatorId: string;
  channelId: string;
  clientMessageId: string;
  content: string;
  contentType?: CommunityChannelMessageTextContentType;
  /**
   * When provided, the new message is a reply scoped under the
   * given thread anchor. The server populates `level` accordingly
   * and bumps the parent's `replyCount` / `lastRepliedAt`.
   */
  parentMessageId?: string;
  /**
   * Media attachments (URLs already uploaded). The caller is
   * responsible for uploading the files via `uploadMedia` before
   * passing them here — the API expects fully-resolved CDN URLs.
   */
  attachments?: CommunityChannelMessageAttachment[];
  /**
   * Link attachments (post cards today). Snapshots — no upload step.
   * The server stores them on `content.links` as opaque objects.
   */
  links?: CommunityChannelMessageLink[];
}): Promise<{ id: string }> {
  const api = withClientAPI(CommunityChannelApi);
  const res = await api.communityChannelApiControllerAddCommunityChannelMessage(
    {
      sellerId: creatorId,
      channelId,
      AddCommunityChannelMessageRequest: {
        clientMessageId,
        parentMessageId,
        content: {
          textContent: {
            type: contentType,
            content,
          },
          attachments: attachments?.length ? attachments : undefined,
          links: links?.length ? links : undefined,
        },
      },
    },
  );
  return { id: res.id };
}
